"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Typography } from "@cappelendamm/design-system/react";
import { PortableText } from "next-sanity";
import Link from "next/link";
import classNames from "classnames";
import Logger from "@cappelendamm/logger";
import { ContributorCard } from "@/contributors/components/ContributorCard";
import { loadLiteraryWorkContributors } from "./contributor-actions";
import { mergeContributors } from "./contributor-pagination";

type ContributorBatch = Awaited<
  ReturnType<typeof loadLiteraryWorkContributors>
>;

export function LiteraryWorkContributorList({
  externalId,
  universeTag,
  initialBatch,
}: {
  externalId: string;
  universeTag: string;
  initialBatch: ContributorBatch;
}) {
  const [contributors, setContributors] = useState<
    ContributorBatch["contributors"]
  >(initialBatch.contributors);
  const [nextOffset, setNextOffset] = useState(initialBatch.nextOffset);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const loadBatch = useCallback(
    async (offset: number, requestGeneration: number) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setIsLoading(true);
      setError(false);
      try {
        const batch = await loadLiteraryWorkContributors({
          externalId,
          universeTag,
          offset,
        });
        if (generation.current !== requestGeneration) return;
        setContributors((previous) =>
          mergeContributors(previous, batch.contributors),
        );
        setNextOffset(batch.nextOffset);
      } catch (error) {
        Logger.error("Failed to load contributor batch:", error);
        if (generation.current === requestGeneration) setError(true);
      } finally {
        if (generation.current === requestGeneration) {
          inFlight.current = false;
          setIsLoading(false);
        }
      }
    },
    [externalId, universeTag],
  );

  useEffect(() => {
    generation.current++;
    inFlight.current = false;
    setContributors(initialBatch.contributors);
    setNextOffset(initialBatch.nextOffset);
    setIsLoading(false);
    setError(false);
    return () => {
      generation.current++;
    };
  }, [initialBatch, externalId, universeTag]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || isLoading || error || nextOffset === null) {
      return;
    }

    let active = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!active || !entries.some((entry) => entry.isIntersecting)) return;
        active = false;
        observer.disconnect();
        void loadBatch(nextOffset, generation.current);
      },
      { rootMargin: "0px 0px 160px 0px" },
    );
    observer.observe(target);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [isLoading, error, nextOffset, loadBatch]);

  const listedContributors = contributors.filter(
    (contributor) => !contributor.isTranslator,
  );

  return (
    <div className="grid gap-4">
      <Typography as="h1" variant="title-lg">
        Bidragsytere
      </Typography>
      {listedContributors.length ? (
        <ul
          className={classNames("grid gap-4", {
            "sm:grid-cols-2": listedContributors.length > 5,
          })}
          aria-busy={isLoading}
        >
          {listedContributors.map((contributor) => (
            <li key={contributor.scid}>
              <ContributorCard
                contributorRole={contributor.role}
                image={contributor.image}
                description={
                  <PortableText
                    value={contributor.description}
                    components={{
                      marks: {
                        link: ({ value, children }) =>
                          value?.href ? (
                            <Link href={value.href}>{children}</Link>
                          ) : (
                            children
                          ),
                      },
                    }}
                  />
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
      <div
        ref={loadMoreRef}
        role="status"
        aria-live="polite"
        className="min-h-6 text-center"
      >
        {listedContributors.length > 0 || nextOffset !== null ? (
          <Typography>
            Viser {listedContributors.length} bidragsytere.
          </Typography>
        ) : null}
        {isLoading ? (
          <Typography>Laster bidragsytere…</Typography>
        ) : nextOffset === null && !listedContributors.length ? (
          <Typography>Ingen bidragsytere funnet.</Typography>
        ) : null}
      </div>
      {error ? <Alert>Kunne ikke laste bidragsytere. Prøv igjen.</Alert> : null}
      {error && nextOffset !== null ? (
        <Button
          type="button"
          variant="secondary"
          disabled={isLoading}
          className="justify-self-center"
          onClick={() => void loadBatch(nextOffset, generation.current)}
        >
          Prøv igjen
        </Button>
      ) : null}
    </div>
  );
}
